/**
 * Shift swap hook — Sprint 19.
 * Manages swap requests, incoming swaps, and admin swap list.
 */

import { useState, useCallback } from 'react';
import { apiFetch } from '../lib/api';

export interface SwapEntry {
  id: number;
  requester_id: number;
  requester_name: string;
  requester_email: string;
  target_id: number;
  target_name: string;
  target_email: string;
  requester_date: string;
  target_date: string;
  reason: string | null;
  status: string;
  reviewed_by: number | null;
  reviewer_name: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface SwapListResponse {
  data: SwapEntry[];
  total: number;
}

export interface SwapCreatePayload {
  target_id: number;
  requester_date: string;
  target_date: string;
  reason?: string;
}

export function useSwaps() {
  const [mySwaps, setMySwaps] = useState<SwapEntry[]>([]);
  const [incoming, setIncoming] = useState<SwapEntry[]>([]);
  const [history, setHistory] = useState<SwapEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMySwaps = useCallback(async (status?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = status ? `?status=${status}` : '';
      const data = await apiFetch<SwapListResponse>(`/api/swaps${params}`);
      setMySwaps(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load swap requests');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchIncoming = useCallback(async (status?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = status ? `?status=${status}` : '';
      const data = await apiFetch<SwapListResponse>(`/api/swaps/incoming${params}`);
      setIncoming(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incoming swaps');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<SwapListResponse>('/api/swaps/history');
      setHistory(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load swap history');
    } finally {
      setLoading(false);
    }
  }, []);

  const createSwap = useCallback(async (payload: SwapCreatePayload) => {
    const data = await apiFetch<{ id: number; status: string }>('/api/swaps', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data;
  }, []);

  const cancelSwap = useCallback(async (swapId: number) => {
    await apiFetch(`/api/swaps/${swapId}/cancel`, { method: 'PUT' });
  }, []);

  const approveSwap = useCallback(async (swapId: number) => {
    await apiFetch(`/api/swaps/${swapId}/approve`, { method: 'PUT' });
  }, []);

  const rejectSwap = useCallback(async (swapId: number) => {
    await apiFetch(`/api/swaps/${swapId}/reject`, { method: 'PUT' });
  }, []);

  return {
    mySwaps,
    incoming,
    history,
    loading,
    error,
    fetchMySwaps,
    fetchIncoming,
    fetchHistory,
    createSwap,
    cancelSwap,
    approveSwap,
    rejectSwap,
  };
}
